import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import type { ResultOf } from "gql.tada";
import { DELETE_CALCULATION } from "../graphql/mutations";

type DeleteCalculationData = ResultOf<typeof DELETE_CALCULATION> | null;

export interface UseDeleteCalculationOptions {
  onSuccess?: () => void;
  onError?: () => void;
}

const reportDeleteError = (error: unknown) => {
  console.error("Failed to delete calculation:", error);
};

export const buildDeleteCalculationHandlers = ({
  onSuccess,
  onError,
}: UseDeleteCalculationOptions) => ({
  onCompleted: (data: DeleteCalculationData) => {
    if (data?.deleteCalculation === true) {
      onSuccess?.();
      return;
    }

    reportDeleteError(
      new Error("deleteCalculation returned no success result"),
    );
    onError?.();
  },
  onError: (error: unknown) => {
    reportDeleteError(error);
    onError?.();
  },
});

const useDeleteCalculation = (options: UseDeleteCalculationOptions = {}) => {
  const [runDelete, { loading }] = useMutation(
    DELETE_CALCULATION,
    buildDeleteCalculationHandlers(options),
  );

  const deleteCalculation = useCallback(
    (id: string) => {
      runDelete({ variables: { id } });
    },
    [runDelete],
  );

  return { deleteCalculation, loading };
};

export default useDeleteCalculation;
